/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { TagDialogHover } from '@components/TagDialog/TagDialogHover';

import { ITooltip } from '@components/Tooltip';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { useState } from '@hookstate/core';
import { equipmentFieldNameDictionary } from '@models/FieldConfiguration.model';
import { IEquipmentInventory } from '@models/EquipmentInventory.model';
import { ConfigProductTable } from '@pages/Products/components/ListProducts/ConfigProductTable/ConfigProductTable';
import { SEARCH_INVENTORY_KEY } from '@pages/Products/Products';
import { indexFieldConfiguration } from '@services/queries/fieldsConfiguration';
import { getUserMembers } from '@services/queries/studios';
import { EquipmentsMap } from '@store/machines/equipmentsPicker.schema';
import { updateRecentViewed } from '@store/redux/features/search';
import { useAppDispatch } from '@store/redux/store';
import { Counter } from '@utils';
import { useContext, useRef, useState as classicUseState } from 'react';
import { UseInfiniteQueryResult, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { NavigationContext } from '@layout/navigation/navigation';
import { IPaginationResult } from '@models/Equipment.model';
import {
  LoadMoreComponent,
  LoadMoreContext,
} from '@components/LoadMoreComponent';
import { LoadMoreCtx } from '@components/LoadMoreComponent/LoadMoreContext';
import { TrContentDef } from '@components/Table/types';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { ScrollToTop } from '@components/ScrollToTop/ScrollToTop';
import { NeededFromEq } from '../OnBoardingModal/OnBoardingModal.utils';

import { ListProductsError } from './error/ListProducts.error';
import { FieldHeader } from './helpers/FieldHeader';
import {
  IconImgAwesome,
  Label,
  ListProductsScrollArea,
  ListProductsTable,
} from './ListProducts.styles';
import {
  ListProductsLoading,
  ListProductsLoadingRows,
} from './loading/ListProducts.loading';
import { TagsListInventory } from './TagsList';
import { EmptyText, getCell } from './helpers/getCell';
import { getTagTip } from './ListProducts.utils';

export interface ListProductsProps {
  result: UseInfiniteQueryResult<IPaginationResult<IEquipmentInventory>>;
  isOnBoarding: boolean;
  equipmentsForOnBoarding: EquipmentsMap;
  updateEquipmentsForOnBoarding: (eq: NeededFromEq, selected: boolean) => void;
  startSearch: (
    input?: string,
    sort?: string,
    direction?: 'ASC' | 'DESC' | undefined,
  ) => void;
  onlyGroupEdits: boolean;
}

export const ONLY_GROUP_EDITS_WARN =
  'Due to performance limitations, once more than  60 items are selected, only group editing will be possible';
const ListProducts = (props: ListProductsProps) => {
  const {
    isOnBoarding,
    equipmentsForOnBoarding,
    updateEquipmentsForOnBoarding,
    startSearch,
    onlyGroupEdits,
    result,
  } = props;

  const {
    data: equipmentsData,
    isLoading,
    isError,
    hasNextPage = false,
    fetchNextPage,
  } = result;
  const { pages = [] } = equipmentsData || {};
  const data = pages
    .map(pageData => pageData.results)
    .reduce(function reduce(prev, next) {
      return prev.concat(next);
    }, []);

  const { studioId } = useContext(NavigationContext);

  const [updateKey, setUpdateKey] = classicUseState(Counter.get());
  const [headerTooltipId] = classicUseState(
    `ProductTable-Header-Tooltip${Date.now()}`,
  );

  const viewport = useRef<HTMLDivElement>(null);

  const { data: configData, isLoading: configIsLoading } = useQuery(
    ['fieldsConfiguration', 'productTable', updateKey],
    () => indexFieldConfiguration('productTable'),
  );

  const membersQuery = useQuery(['members', 'Inventory'], getUserMembers);
  const members = membersQuery.data ?? [];
  const open = useState(false);
  const history = useHistory();

  const loadMoreCtx: LoadMoreCtx = {
    fetchNextPage,
    hasNextPage,
  };

  const dispatch = useAppDispatch();

  const { isDesktop, isMobile } = useSelector(checkDevice());

  if (isLoading || !data || configIsLoading) {
    return <ListProductsLoading />;
  }
  if (isError || !configData) {
    return <ListProductsError />;
  }
  const configFromApi = configData.filter(
    item => item.location === 'productTable',
  );

  function onCellClick(item: IEquipmentInventory, isSelected: boolean) {
    return () => {
      if (isOnBoarding) {
        // Todo: shift+select.
        updateEquipmentsForOnBoarding(item, isSelected);
        return;
      }
      dispatch(
        updateRecentViewed({
          keyHistory: SEARCH_INVENTORY_KEY,
          value: {
            link: `/equipments/${item.id}`,
            labelOne: item.product.model ? item.product.model : '',
            thumbnail: item.product.imageUrl ?? undefined,
            labelTwo: item.serial ? item.serial : '',
            studioId,
          },
        }),
      );
      history.push(`/equipments/${item.id}`);
    };
  }

  function getScrollClassNames() {
    if (isDesktop) return 'bscroll';
    return '';
  }

  return (
    <ListProductsScrollArea
      ref={viewport}
      className={`reset-global new-icon ${getScrollClassNames()}`}
    >
      <ListProductsTable data-configlen={configFromApi.length}>
        <thead>
          <tr>
            {configFromApi.map((item, index, array) => {
              const isFirst = index === 0;
              const isLast = index === array.length - 1;
              const classList = [isFirst && 'first', isLast && 'last'];
              const className = classList.filter(_item => !!_item).join(' ');
              const isCustomField = item.customField !== undefined;

              const tagTip = getTagTip(false, isCustomField);

              classList.push('config');
              return (
                <th
                  key={item.id}
                  data-fieldname={item.fieldName}
                  className={className}
                >
                  <FieldHeader
                    label={item.name}
                    sortString={item.fieldFilter}
                    disabled={isCustomField}
                    startSearch={startSearch}
                    tooltipId={headerTooltipId}
                    tooltipText={tagTip}
                  />
                </th>
              );
            })}
            <th key="Tags" data-fieldname="Tags">
              <FieldHeader
                label="Tags"
                sortString="tags.name"
                disabled
                startSearch={startSearch}
                tooltipId={headerTooltipId}
                tooltipText={getTagTip(true)}
                rightIcon={
                  isDesktop && (
                    <IconImgAwesome
                      data-testid="field-config-button"
                      icon={faCog}
                      onClick={() => open.set(true)}
                      color="#8F95B2"
                    />
                  )
                }
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map(item => {
            const configCells = configFromApi.map<TrContentDef>(
              (config, colIndex) => {
                return {
                  id: config.id,
                  node: getCell(config, item, colIndex, members, isMobile),
                };
              },
            );
            const extras = [
              {
                id: 'Tags',
                node:
                  item.tags.length > 0 ? (
                    <TagDialogHover tags={item.tags}>
                      <TagsListInventory tags={item.tags} />
                    </TagDialogHover>
                  ) : (
                    <Label>
                      <EmptyText>Empty</EmptyText>
                    </Label>
                  ),
              },
            ];
            const cells = configCells.concat(extras);

            const isSelected = equipmentsForOnBoarding.has(item.id);

            return (
              <ListProductsTable.Tr
                key={item.id}
                data-selected={isSelected}
                onClick={onCellClick(item, isSelected)}
                cells={cells}
                data-for="onboarding-over-tooltip"
                data-tip={!onlyGroupEdits ? '' : ONLY_GROUP_EDITS_WARN}
                data-testid="inventory-equipment"
              />
            );
          })}
        </tbody>
      </ListProductsTable>
      {isMobile && <ScrollToTop elementRef={viewport} />}
      {isDesktop && (
        <ConfigProductTable
          openAddField={open}
          initialConfigData={configFromApi}
          updateConfiguration={() => {
            setUpdateKey(Counter.next());
          }}
          location="productTable"
          similarLocation="productHeader"
          fieldNameDictionary={equipmentFieldNameDictionary}
        />
      )}
      <ITooltip
        id="onboarding-over-tooltip"
        effect="float"
        delayShow={300}
        delayHide={300}
      />
      <ITooltip id={headerTooltipId} place="top" />
      <LoadMoreContext.Provider value={loadMoreCtx}>
        <LoadMoreComponent
          content={isMobile && <ListProductsLoadingRows height={34} />}
        />
      </LoadMoreContext.Provider>
    </ListProductsScrollArea>
  );
};

export { ListProducts };
